import React, { useEffect, useState } from "react";
import {
  DialogActions,
  DialogContent,
  Grid,
  useTheme,
} from "@material-ui/core";
import * as R from "ramda";
import Input from "Components/Input/Input";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import Translator from "Languages/Utility/Translator";
import { useDispatch } from "react-redux";
import { saveNewUnitInfo } from "Stores/actions";
import { useSetState } from "Helpers/Hooks";
import { read } from "Helpers/LocalStorage";
import SelectPattern from "./SelectPattern";
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import NumberMask from "Components/Input/components/NumberMask";
import millimeterMask from "Components/Input/components/millimeterMask";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiIconButton from "Components/MuiIconButton/MuiIconButton";

export default function NewUnitFastMode({
  saveNewUnit,
  onExit,
  onSelectPattern,
  controls,
  setValue,
}) {
  const { t } = useTranslation();
  const { direction } = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectPattern, setSelectPattern] = useState(0);
  const [formValid, setFormValid] = useState({
    unitWidth: true,
    unitHeight: true,
  });
  const [ctrlSize, setCtrlSize] = useSetState({
    unitWidth: {
      type: "millimeter",
      value: 2000,
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
        min: 200,
        max: 20000,
      },
    },
    unitHeight: {
      type: "millimeter",
      value: 1000,
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
        min: 200,
        max: 20000,
      },
    },
  });

  useEffect(() => {
    read("PatternDefault") ? setSelectPattern(2) : setSelectPattern(1);
  }, []);

  const onActionSelectPattern = (value) => {
    onSelectPattern(value);
    setSelectPattern(2);
  };

  const setSizeValue = (name, value, validation) => {
    setCtrlSize({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const onSubmit = async () => {
    if (
      !loading &&
      read("PatternDefault") &&
      !R.isEmpty(formValid) &&
      Object.values(formValid).every((item) => item === true)
    ) {
      setLoading(true);
      const height = ctrlSize.unitHeight.value - controls.windGapHeight.value;
      const width = ctrlSize.unitWidth.value - controls.windGapWidth.value;
      const designShape = `[{"Startpoint":"0,0","Endpoint":"0,${height}","Ctrlpoint":"0,0","Model":"FRAME","X":"0","Y":"(3.y)","C":"0","Z":"0","Iff":"0"},{"Startpoint":"0,${height}","Endpoint":"${width},${height}",
            "Ctrlpoint":"0,0","Model":"FRAME","X":"-1","Y":"0","C":"0","Z":"0","Iff":"(2.x)#[(3.y)/(20)]"},{"Startpoint":"${width},${height}","Endpoint":"${width},0",
            "Ctrlpoint":"0,0","Model":"FRAME","X":"0","Y":"-1","C":"0","Z":"0","Iff":"(3.y)#[(2.x)/(20)]"},{"Startpoint":"${width},0","Endpoint":"0,0","Ctrlpoint":"0,0","Model":"FRAME","X":"(2.x)","Y":"0","C":"0","Z":"0","Iff":"0"}]`;
      await dispatch(saveNewUnitInfo(designShape));
      await saveNewUnit({ height, width }, designShape);
      setLoading(false);
    } else if (!read("PatternDefault")) {
      showNotify({
        text: t("select_pattern"),
        customClass: "error",
      });
    }
  };

  const onChangeValue = (name, value, validation) => {
    if (name === "windGapWidth") {
      if (
        value <
        ctrlSize.unitWidth.value - (ctrlSize.unitWidth.value * 5) / 100
      ) {
        setValue(name, value, validation);
      } else {
        showNotify({
          text: t("windWidthError"),
          customClass: "error",
        });
      }
    } else if (name === "windGapHeight") {
      if (
        value <
        ctrlSize.unitHeight.value - (ctrlSize.unitHeight.value * 5) / 100
      ) {
        setValue(name, value, validation);
      } else {
        showNotify({
          text: t("windHeightError"),
          customClass: "error",
        });
      }
    } else {
      setValue(name, value, validation);
    }
  };

  return (
    <>
      <div className="dialogHeader">
        <p>{t("addNewSquareUnit")}</p>
      </div>
      <DialogContent
        className={direction === "rtl" ? "text-right" : "text-left"}
        id="content-dialog-manager"
      >
        <BackDropLoading loading={loading} />
        <Grid container spacing={2}>
          {Object.keys(controls).map((key, index) => {
            return (
              <Grid
                item
                sm={controls[key].col.sm}
                xs={controls[key].col.xs}
                key={key + index}
              >
                <Input
                  setValue={onChangeValue.bind(this)} //setValue.bind(this)}
                  name={key}
                  validation={controls[key].validation}
                  label={<Translator id={key} />}
                  type={controls[key].type}
                  value={controls[key].value}
                  minLength={controls[key]?.validation?.minLength}
                  maxlength={controls[key]?.validation?.maxLength}
                  endIcon={
                    controls[key].type === "millimeter" ? (
                      <Translator id="millimeter" />
                    ) : (
                      ""
                    )
                  }
                  inputComponent={
                    controls[key].type === "millimeter"
                      ? millimeterMask
                      : controls[key].type === "number"
                      ? NumberMask
                      : ""
                  }
                />
              </Grid>
            );
          })}
        </Grid>
        <Grid container className="d-flex g-2 mt-2 mb-3">
          {Object.keys(ctrlSize).map((key, index) => {
            return (
              <Grid item key={key + index} className="flex-grow-1">
                <Input
                  setValue={setSizeValue.bind(this)}
                  name={key}
                  value={ctrlSize[key].value}
                  validation={ctrlSize[key].validation}
                  label={<Translator id={key} />}
                  type={ctrlSize[key].type}
                  endIcon={<Translator id="millimeter" />}
                  inputComponent={millimeterMask}
                />
              </Grid>
            );
          })}
        </Grid>
        {selectPattern === 2 ? (
          <Grid item xs={12} className="info-alert-dialog primary">
            <span>{`${t("selected_pattern")} :`}</span>
            <p className="normalTitle">{read("PatternDefault").Name}</p>
            <div className="info-alert-action">
              <MuiIconButton
                className="warning-fill"
                icon={<Icons.X />}
                title={t("list_olgo")}
                onClick={() => setSelectPattern(1)}
              />
            </div>
          </Grid>
        ) : (
          <SelectPattern onSelect={onActionSelectPattern} />
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton
          color="primary"
          type="submit"
          title={<Translator id="button_save" />}
          innerIcon={<Icons.Floppy />}
          onClick={onSubmit}
        />
        <MuiButton onClick={onExit} title={<Translator id="enseraf" />} />
      </DialogActions>
    </>
  );
}
